import { useId } from 'react'
import { Button as ButtonComponent } from '@/components/ui/button'
import { Checkbox as CheckboxComponent } from '@/components/ui/checkbox'
import { CubeLoader as CubeLoaderComponent } from '@/components/ui/cube-loader'
import { cn } from '@/lib/utils'
import { ButtonProps, CubeLoaderProps } from '@/types'
import { activeSourcesOf, itemLinesOf, kindLabelOf, sourceLabelOf, sourceStateNote } from './basisUtils'
import type { GenerationBasisController } from './useGenerationBasis'
import type { GenerationBasisItem, GenerationBasisSource } from './types'

// Type casts for the UI components from JSX files
const Button = ButtonComponent as React.FC<
    ButtonProps & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'aria-label' | 'aria-pressed'>
>
const Checkbox = CheckboxComponent as React.FC<{
    id?: string
    checked?: boolean
    onCheckedChange?: (checked: boolean) => void
    disabled?: boolean
    className?: string
}>
const CubeLoader = CubeLoaderComponent as React.FC<CubeLoaderProps>

interface GenerationBasisPanelProps {
    /** The state and actions of the basis, from useGenerationBasis */
    basis: GenerationBasisController
    title?: string
    /** Extra line for an item, such as the results of a winning ad; shown under its text */
    evidenceLabel?: (item: GenerationBasisItem) => string | null
    className?: string
}

const MODE_TEXT = {
    manual: 'Manual: only the items you approve are used.',
    automated: 'Automated: everything that qualifies is used, except what you exclude.',
} as const

interface ItemRowProps {
    item: GenerationBasisItem
    disabled: boolean
    evidenceLabel?: (item: GenerationBasisItem) => string | null
    children: React.ReactNode
}

const ItemRow = ({ item, disabled, evidenceLabel, children }: ItemRowProps) => {
    const lines = itemLinesOf(item)
    const evidence = evidenceLabel?.(item) ?? null
    return (
        <li
            className={cn('tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-2 tw:px-3 tw:py-2', {
                'tw:opacity-60': disabled,
            })}
            data-testid={`generation-basis-item-${item.key}`}
        >
            <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
                <span className="tw:text-xs tw:text-gray-200">{kindLabelOf(item.kind)}</span>
                {lines.map((line, index) => (
                    <span key={index} className="tw:text-sm tw:break-words">
                        {line}
                    </span>
                ))}
                {evidence && <span className="tw:text-xs tw:text-gray-200">{evidence}</span>}
            </div>
            <div className="tw:flex tw:shrink-0 tw:gap-1">{children}</div>
        </li>
    )
}

interface ItemActionProps {
    label: string
    item: GenerationBasisItem
    onClick: () => void
    disabled: boolean
    variant?: ButtonProps['variant']
}

const ItemAction = ({ label, item, onClick, disabled, variant = 'ghost' }: ItemActionProps) => {
    const lines = itemLinesOf(item)
    return (
        <Button
            type="button"
            variant={variant}
            size="sm"
            onClick={onClick}
            disabled={disabled}
            aria-label={`${label}: ${lines.join(' ')}`}
        >
            {label}
        </Button>
    )
}

interface ItemGroupProps {
    heading: string
    items: GenerationBasisItem[]
    controller: GenerationBasisController
    source: GenerationBasisSource
    evidenceLabel?: (item: GenerationBasisItem) => string | null
}

const ItemGroup = ({ heading, items, controller, source, evidenceLabel }: ItemGroupProps) => {
    if (items.length === 0) return null
    const { decide, blocked, skip } = controller
    const manual = source.mode === 'manual'
    return (
        <div className="tw:flex tw:flex-col tw:gap-1">
            <span className="tw:text-xs tw:font-semibold tw:text-gray-200">{heading}</span>
            <ul className="tw:flex tw:list-none tw:flex-col tw:divide-y tw:rounded tw:border tw:!p-0 tw:!m-0">
                {items.map((item) => (
                    <ItemRow key={item.key} item={item} disabled={skip} evidenceLabel={evidenceLabel}>
                        {item.state === 'excluded' && (
                            <ItemAction
                                label="Restore"
                                item={item}
                                disabled={blocked}
                                onClick={() => decide(source.source, item.key, null)}
                            />
                        )}
                        {item.state === 'proposed' && manual && (
                            <ItemAction
                                label="Approve"
                                item={item}
                                disabled={blocked}
                                variant="secondary"
                                onClick={() => decide(source.source, item.key, 'APPROVED')}
                            />
                        )}
                        {item.state !== 'excluded' && (
                            <ItemAction
                                label="Exclude"
                                item={item}
                                disabled={blocked}
                                onClick={() => decide(source.source, item.key, 'EXCLUDED')}
                            />
                        )}
                    </ItemRow>
                ))}
            </ul>
        </div>
    )
}

interface SourceSectionProps {
    source: GenerationBasisSource
    items: GenerationBasisItem[]
    controller: GenerationBasisController
    evidenceLabel?: (item: GenerationBasisItem) => string | null
}

const SourceSection = ({ source, items, controller, evidenceLabel }: SourceSectionProps) => {
    const { blocked, setMode, approveAll } = controller
    const manual = source.mode === 'manual'
    const used = items.filter((item) => item.state === 'used')
    const proposed = items.filter((item) => item.state === 'proposed')
    const excluded = items.filter((item) => item.state === 'excluded')
    const stateNote = sourceStateNote(source.state, source.reason, source.source)
    const label = sourceLabelOf(source.source)
    const mode = source.mode === 'automated' ? 'automated' : 'manual'

    return (
        <section
            className="tw:flex tw:flex-col tw:gap-2"
            aria-label={label}
            data-testid={`generation-basis-${source.source}`}
        >
            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
                <div className="tw:flex tw:flex-col">
                    <span className="tw:text-sm tw:font-semibold">{label}</span>
                    <span className="tw:text-xs tw:text-gray-200">{MODE_TEXT[mode]}</span>
                </div>
                {source.automatedAllowed && (
                    <div className="tw:flex tw:gap-1" role="group" aria-label={`${label} mode`}>
                        <Button
                            type="button"
                            size="sm"
                            variant={manual ? 'default' : 'secondary'}
                            aria-pressed={manual}
                            disabled={blocked}
                            onClick={() => !manual && setMode(source.source, 'manual')}
                        >
                            Manual
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant={manual ? 'secondary' : 'default'}
                            aria-pressed={!manual}
                            disabled={blocked}
                            onClick={() => manual && setMode(source.source, 'automated')}
                        >
                            Automated
                        </Button>
                    </div>
                )}
            </div>

            {stateNote && (
                <p className="tw:m-0 tw:text-sm tw:text-yellow-300" role="status">
                    {stateNote}
                </p>
            )}

            {items.length === 0 && !stateNote && (
                <p className="tw:m-0 tw:text-sm tw:text-gray-200">Nothing is available to use yet.</p>
            )}

            <ItemGroup
                heading={manual ? 'Approved: used' : 'Based on: used'}
                items={used}
                controller={controller}
                source={source}
                evidenceLabel={evidenceLabel}
            />
            <ItemGroup
                heading={manual ? 'Proposed: not used until you approve' : 'Not applied'}
                items={proposed}
                controller={controller}
                source={source}
                evidenceLabel={evidenceLabel}
            />
            <ItemGroup
                heading="Excluded"
                items={excluded}
                controller={controller}
                source={source}
                evidenceLabel={evidenceLabel}
            />

            {manual && proposed.length > 1 && (
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="tw:w-fit"
                    disabled={blocked}
                    onClick={() => approveAll(source.source)}
                >
                    Approve all
                </Button>
            )}
        </section>
    )
}

/** "Generate without insight": one choice for every source, kept until the user unticks it. */
const SkipChoice = ({ basis }: { basis: GenerationBasisController }) => {
    const skipId = useId()
    return (
        <div className="tw:flex tw:items-start tw:gap-2">
            <Checkbox
                id={skipId}
                checked={basis.skip}
                onCheckedChange={(checked) => basis.setSkip(checked === true)}
                disabled={basis.blocked}
                className="tw:mt-0.5"
                aria-describedby={`${skipId}-note`}
            />
            <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
                <label htmlFor={skipId} className="tw:m-0 tw:text-sm">
                    Generate without insight
                </label>
                <p id={`${skipId}-note`} className="tw:m-0 tw:text-xs tw:text-gray-200">
                    Nothing from the {basis.noun} is used while this is ticked.
                </p>
            </div>
        </div>
    )
}

/**
 * What a generation is based on, before it runs: for each source that is switched on, its items as used, proposed or
 * excluded, with Approve / Exclude / Restore, the manual or automated choice (where staff allow automated) and
 * "Generate without insight". Shows nothing while no source is switched on. The owner of the screen sends
 * `basis.request` with its generation and keeps its Generate button disabled while `basis.blocked`.
 */
const GenerationBasisPanel = ({
    basis,
    title = 'Audience insight',
    evidenceLabel,
    className,
}: GenerationBasisPanelProps) => {
    const { status, blocked } = basis

    if (status === 'idle') return null

    // Nothing is switched on, but a skip the user chose stays visible: it is still sent and can be unticked
    if (status === 'off') {
        return basis.skip ? (
            <div className={cn('tw:flex tw:flex-col tw:gap-1', className)} data-testid="generation-basis-panel">
                <SkipChoice basis={basis} />
            </div>
        ) : null
    }

    if (status === 'loading') {
        return (
            <div
                className={cn('tw:flex tw:items-center tw:gap-3', className)}
                role="status"
                data-testid="generation-basis-panel"
            >
                <CubeLoader size="sm" />
                <span className="tw:text-sm">Loading {basis.noun}…</span>
            </div>
        )
    }

    if (status === 'error') {
        return (
            <div
                className={cn('tw:flex tw:flex-col tw:items-start tw:gap-2', className)}
                data-testid="generation-basis-panel"
            >
                <p className="tw:m-0 tw:text-sm tw:text-red-300" role="alert">
                    The {basis.noun} could not be read right now. Generating is paused until it can.
                </p>
                <Button type="button" variant="secondary" size="sm" onClick={() => basis.reload()}>
                    Try again
                </Button>
            </div>
        )
    }

    const sources = basis.basis ? activeSourcesOf(basis.basis) : []
    const items = basis.basis?.items ?? []

    return (
        <div
            className={cn('tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:p-3', className)}
            data-testid="generation-basis-panel"
            aria-busy={blocked}
        >
            <span className="tw:text-sm tw:font-semibold">{title}</span>

            {sources.map((source) => (
                <SourceSection
                    key={source.source}
                    source={source}
                    items={items.filter((item) => item.source === source.source)}
                    controller={basis}
                    evidenceLabel={evidenceLabel}
                />
            ))}

            <SkipChoice basis={basis} />

            {blocked && (
                <p className="tw:m-0 tw:text-xs tw:text-gray-200" role="status">
                    Updating {basis.noun}…
                </p>
            )}
            {basis.message && (
                <p className="tw:m-0 tw:text-sm tw:text-red-300" role="alert">
                    {basis.message}
                </p>
            )}
        </div>
    )
}

export default GenerationBasisPanel
